import { Injectable } from '@angular/core';
import { HttpMethodType } from '@app/app.constant';
import { HttpCallService } from '@core/services/http-call.service';
import {
  ISaveTerminologyOverridesRequestHttp,
  ITerminologyLayersResponseHttp,
  ITerminologyResponseHttp,
  ITerminologyTermInfoHttp,
} from '@app/models/terminology/terminology.model';
import { BehaviorSubject, Observable, of, tap } from 'rxjs';

/**
 * Defaults for terms added after the catalogue was first seeded, used only when an environment's
 * catalogue does not have the row yet (its seed script not run) - so the screen shows the platform
 * wording, never the raw key. The catalogue row, once present, wins.
 */
const BUILT_IN_DEFAULTS: { [key: string]: string } = {
  correspondence_log_singular: 'Correspondence Log',
  correspondence_log_plural: 'Correspondence Logs',
  non_case_load: 'Non Case Load',
};

const HttpServiceMethods = {
  GetTerminology: 'Terminology/GetTerminology',
  SaveOverrides: 'Terminology/SaveOverrides',
  GetTerminologyLayers: 'Terminology/GetTerminologyLayers',
};

/**
 * Holds the current session's resolved Evide terminology so every UI consumer
 * (templates via `TermPipe`, programmatic callers, the back-compat shim in
 * `App.contactLabel*`) sees the same map. Loaded once on login; refreshed
 * after the admin terminology save.
 *
 * Reads default to the canonical Evide values when the bundle hasn't loaded
 * yet — every consumer should fall back gracefully so first paint never blocks
 * on the network call.
 */
@Injectable({ providedIn: 'root' })
export class TerminologyService {
  /** Resolved term-key → display value. Populated by `load()`. */
  private readonly resolved$ = new BehaviorSubject<{ [key: string]: string }>({});

  /** Catalogue of known terms — used by the admin screen. */
  private readonly catalogue$ = new BehaviorSubject<ITerminologyTermInfoHttp[]>([]);

  constructor(private http: HttpCallService) {}

  /**
   * Fetches the current session's resolved terminology and caches it. Call once
   * on login (and again after an admin save) so the cache reflects what the
   * backend would currently return.
   */
  public load(): Observable<ITerminologyResponseHttp> {
    return (this.http.callService(HttpMethodType.GET, HttpServiceMethods.GetTerminology) as Observable<ITerminologyResponseHttp>).pipe(
      tap((res) => {
        this.resolved$.next(res?.resolved ?? {});
        this.catalogue$.next(res?.terms ?? []);
      })
    );
  }

  /**
   * Synchronous lookup. Returns the resolved value for `termKey`, falling back
   * to the default canonical value in the catalogue, then to a sensible
   * placeholder. Safe to call before `load()` completes.
   */
  public get(termKey: string): string {
    const resolved = this.resolved$.getValue()[termKey];
    if (resolved) return resolved;
    const meta = this.catalogue$.getValue().find((t) => t.termKey === termKey);
    return meta?.defaultValue ?? BUILT_IN_DEFAULTS[termKey] ?? termKey;
  }

  /**
   * Swaps the platform's default wording for this licence's terms inside a fixed label, for label
   * lists defined once at load (dashboard KPI names, data-view names, widget links): "Unique Contacts"
   * becomes "Unique Young People". Whole words only; a default's capitalised and lower-case forms are
   * both handled, and the American "Program(s)" spelling counts as the programme term.
   */
  public applyTerms(text: string): string {
    if (!text) return text;
    const pairs: [string, string][] = [];
    for (const term of this.catalogue$.getValue()) {
      const value = this.get(term.termKey);
      if (term.defaultValue && value && value !== term.defaultValue) pairs.push([term.defaultValue, value]);
    }
    const programme = this.get('programme_singular');
    const programmes = this.get('programme_plural');
    if (programme && programme !== 'Programme') pairs.push(['Program', programme]);
    if (programmes && programmes !== 'Programmes') pairs.push(['Programs', programmes]);
    let out = text;
    for (const [from, to] of pairs) {
      const esc = from.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      out = out.replace(new RegExp(`\\b${esc}\\b`, 'g'), to);
      const lowerFrom = from.toLowerCase();
      if (lowerFrom !== from) {
        out = out.replace(new RegExp(`\\b${lowerFrom.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g'), to.toLowerCase());
      }
    }
    return out;
  }

  /**
   * The editor's read: default, licence label and project label per term, for one licence (null = the
   * signed-in one) and optionally one project. Not cached - the editor always shows what is saved.
   */
  public getLayers(licenseId?: number | null, projectId?: number | null): Observable<ITerminologyLayersResponseHttp> {
    const query: string[] = [];
    if (licenseId) query.push(`licenseId=${licenseId}`);
    if (projectId) query.push(`projectId=${projectId}`);
    const url = HttpServiceMethods.GetTerminologyLayers + (query.length ? `?${query.join("&")}` : "");
    return this.http.callService(HttpMethodType.GET, url) as Observable<ITerminologyLayersResponseHttp>;
  }

  /** Reactive variant of `get()` — emits when the cache updates. */
  public get$(termKey: string): Observable<string> {
    return new Observable<string>((sub) => {
      const emit = () => sub.next(this.get(termKey));
      const subA = this.resolved$.subscribe(emit);
      const subB = this.catalogue$.subscribe(emit);
      emit();
      return () => {
        subA.unsubscribe();
        subB.unsubscribe();
      };
    });
  }

  /** Catalogue of every known term — used by the admin screen to render rows. */
  public getCatalogue(): ITerminologyTermInfoHttp[] {
    return this.catalogue$.getValue();
  }

  public catalogue(): Observable<ITerminologyTermInfoHttp[]> {
    return this.catalogue$.asObservable();
  }

  /** Resolved-map snapshot. */
  public getAllResolved(): { [key: string]: string } {
    return this.resolved$.getValue();
  }

  /**
   * Pushes a batch of overrides up to the backend. Empty `customValue` removes
   * the override for that term. The service re-fetches the bundle on success
   * so the in-memory cache reflects what the next consumer would see.
   */
  public saveOverrides(request: ISaveTerminologyOverridesRequestHttp): Observable<boolean> {
    return (this.http.callService(HttpMethodType.POST, HttpServiceMethods.SaveOverrides, request) as Observable<boolean>).pipe(
      tap((ok) => {
        if (ok) {
          // Fire-and-forget refresh; ok if it lands after the caller has navigated away.
          this.load().subscribe({ error: () => of(null) });
        }
      })
    );
  }
}
